import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Drawer, DatePicker, Input, Button } from 'antd';
import moment from 'moment';

import { PATHS } from 'router/config';

import { confirmModal } from '../confirm';
import { IStaffTask, isClosed, studentName } from './taskBuckets';
import * as S from './TaskBoard.styled';

const fmtTs = (iso?: string | null): string =>
  iso ? moment(iso).format('DD.MM.YYYY HH:mm') : '—';

interface ITaskDetailsDrawerProps {
  task: IStaffTask | null;
  visible: boolean;
  onClose: () => void;
  onToggleDone: (task: IStaffTask) => void;
  onReschedule: (taskId: string, dueDate: string) => void;
  onSaveDescription: (taskId: string, description: string) => Promise<boolean>;
  onDelete: (taskId: string) => void;
  mutating: boolean;
}

/**
 * Right-side task details panel: view + quick actions (done/reopen,
 * reschedule, edit description, delete) — intentionally not a full edit form.
 * The parent owns the mutations; the backend treats a null due date as
 * "don't change", so the date cannot be cleared (`allowClear={false}`).
 */
export const TaskDetailsDrawer = memo(
  ({
    task,
    visible,
    onClose,
    onToggleDone,
    onReschedule,
    onSaveDescription,
    onDelete,
    mutating,
  }: ITaskDetailsDrawerProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const [editingDesc, setEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState('');

    const taskId = task?.id;
    useEffect(() => {
      setEditingDesc(false);
      setDescDraft('');
    }, [taskId]);

    if (!task) return null;

    const done = isClosed(task);
    const studName = studentName(task);

    const openGroup = (id: string) =>
      history.push(`${PATHS.groupDetail.replace(':id', id)}?tab=tasks`);
    const openStudent = (id: string) =>
      history.push(`${PATHS.studentDetail.replace(':id', id)}?tab=tasks`);

    const saveDesc = async () => {
      const ok = await onSaveDescription(task.id, descDraft);
      if (ok) setEditingDesc(false);
    };

    const remove = async () => {
      const ok = await confirmModal(t('pages.tasks.drawer.deleteConfirm'));
      if (ok) onDelete(task.id);
    };

    return (
      <Drawer
        visible={visible}
        onClose={onClose}
        width={480}
        placement="right"
        destroyOnClose
        title={t('pages.tasks.drawer.title')}
      >
        <S.DrawerTitleRow>
          <S.Chip>{t(`pages.tasks.status.${task.status}`)}</S.Chip>
          {task.priority === 'high' ? (
            <S.Chip tone="warn">{t('pages.tasks.priority.high')}</S.Chip>
          ) : null}
        </S.DrawerTitleRow>
        <S.DrawerTaskTitle>{task.title}</S.DrawerTaskTitle>

        <S.DrawerSection>
          <S.DrawerLabel>{t('pages.tasks.drawer.due')}</S.DrawerLabel>
          <DatePicker
            value={task.dueDate ? moment(task.dueDate) : undefined}
            allowClear={false}
            disabled={mutating}
            onChange={(m) => {
              if (m) onReschedule(task.id, m.format('YYYY-MM-DD'));
            }}
          />
        </S.DrawerSection>

        <S.DrawerSection>
          <S.DrawerLabel>
            {t('pages.tasks.drawer.description')}{' '}
            {!editingDesc ? (
              <S.EditLink
                type="button"
                onClick={() => {
                  setDescDraft(task.description || '');
                  setEditingDesc(true);
                }}
              >
                {t('pages.tasks.drawer.editDescription')}
              </S.EditLink>
            ) : null}
          </S.DrawerLabel>
          {editingDesc ? (
            <>
              <Input.TextArea
                rows={4}
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
              />
              <S.DrawerFooter>
                <Button
                  type="primary"
                  size="small"
                  loading={mutating}
                  onClick={saveDesc}
                >
                  {t('pages.tasks.drawer.save')}
                </Button>
                <Button size="small" onClick={() => setEditingDesc(false)}>
                  {t('pages.tasks.drawer.cancel')}
                </Button>
              </S.DrawerFooter>
            </>
          ) : (
            <S.DrawerDescription>
              {task.description || (
                <S.Muted as="span">
                  {t('pages.tasks.drawer.noDescription')}
                </S.Muted>
              )}
            </S.DrawerDescription>
          )}
        </S.DrawerSection>

        <S.DrawerSection>
          {task.group ? (
            <S.MetaRow>
              <span>{t('pages.tasks.drawer.group')}</span>
              <S.MetaLink
                type="button"
                onClick={() => openGroup(task.group!.id)}
              >
                {task.group.name}
              </S.MetaLink>
            </S.MetaRow>
          ) : null}
          {task.student ? (
            <S.MetaRow>
              <span>{t('pages.tasks.drawer.student')}</span>
              <S.MetaLink
                type="button"
                onClick={() => openStudent(task.student!.id)}
              >
                {studName || '—'}
              </S.MetaLink>
            </S.MetaRow>
          ) : null}
          {task.agreement?.agreementTitle ? (
            <S.MetaRow>
              <span>{t('pages.tasks.drawer.agreement')}</span>
              <span>{task.agreement.agreementTitle}</span>
            </S.MetaRow>
          ) : null}
        </S.DrawerSection>

        <S.DrawerSection>
          <S.MetaRow>
            <span>{t('pages.tasks.assignee')}</span>
            <span>{task.assigneeName || '—'}</span>
          </S.MetaRow>
          <S.MetaRow>
            <span>{t('pages.tasks.drawer.author')}</span>
            <span>{task.createdByName || '—'}</span>
          </S.MetaRow>
          <S.MetaRow>
            <span>{t('pages.tasks.drawer.createdAt')}</span>
            <span>{fmtTs(task.createdAt)}</span>
          </S.MetaRow>
          <S.MetaRow>
            <span>{t('pages.tasks.drawer.updatedAt')}</span>
            <span>{fmtTs(task.updatedAt)}</span>
          </S.MetaRow>
          {task.completedAt ? (
            <S.MetaRow>
              <span>{t('pages.tasks.drawer.completedAt')}</span>
              <span>{fmtTs(task.completedAt)}</span>
            </S.MetaRow>
          ) : null}
        </S.DrawerSection>

        <S.DrawerFooter>
          <Button
            type="primary"
            loading={mutating}
            onClick={() => onToggleDone(task)}
          >
            {done ? t('pages.tasks.reopen') : t('pages.tasks.markDone')}
          </Button>
          <Button danger disabled={mutating} onClick={remove}>
            {t('pages.tasks.delete')}
          </Button>
        </S.DrawerFooter>
      </Drawer>
    );
  },
);

TaskDetailsDrawer.displayName = 'TaskDetailsDrawer';
